import { STATUS } from "@/constants/statuses";
import { REWARDS_DISCOUNT_TYPES } from "@apps/rewards/constants/rewardsDiscountTypes";
import { getLoadingStatus } from "@apps/rewards/utils/statuses";
import { applyTransaction } from "@datorama/akita";
import WindowPopupManager from "@lib/WindowPopupManager/WindowPopupManager";
import type ProductEntity from "@modules/products/entities/ProductEntity";
import { TokenManagerModule } from "@modules/token_manager/TokenManagerModule";
import type { Observable } from "rxjs";
import { catchError, combineLatest, filter, map, tap } from "rxjs";
import { inject, singleton } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";

import { REFERRAL_SHARE_PATH } from "../../../constants/constants";
import { REWARDS_REWARD_TYPES } from "../../../constants/rewardsRewardTypes";
import { rewardsApplicationLoggerToken } from "../../../tokens";
import { getShareUrl } from "../../../utils/getShareUrl";
import type { ProgramAccountEntity } from "../../ProgramAccount/entities/RewardsProgramAccountEntity";
import type {
    RewardsReferralEmailShareEntity,
    RewardsStorefrontValuesSettingsEntity,
} from "../../StorefrontValues/models/RewardsStorefrontValuesEntities";
import RewardsReferralProgramApiService from "../api_services/RewardsReferralProgramApiService";
import {
    REWARDS_REFERRAL_SHARE_CHANNELS,
    REWARDS_REFERRAL_STEPS_TRANSLATION_KEYS,
} from "../constants";
import type {
    RewardsReferralProgramEntity,
    RewardsReferralProgramRewardEntity,
} from "../models/RewardsReferralProgramEntities";
import RewardsReferralProgramStore from "../stores/RewardsReferralProgramStore";
import type { REWARDS_REFERRAL_SHARE_CHANNELS_TYPE } from "../types";

@singleton()
class RewardsReferralProgramService {
    constructor(
        @inject(rewardsApplicationLoggerToken)
        private readonly logger: LoggerInterface,
        private readonly referralProgramApiService: RewardsReferralProgramApiService,
        private readonly referralProgramStore: RewardsReferralProgramStore,
        private readonly windowPopupManager: WindowPopupManager,
        private readonly tokenManagerModule: TokenManagerModule
    ) {}

    public selectReferralSteps = (
        isReferralReward$: Observable<boolean>
    ): Observable<readonly string[]> =>
        isReferralReward$.pipe(
            map((isReferralReward) =>
                isReferralReward
                    ? REWARDS_REFERRAL_STEPS_TRANSLATION_KEYS
                    : REWARDS_REFERRAL_STEPS_TRANSLATION_KEYS.slice(0, 2)
            )
        );

    public handleOpenReferralShareWindow = (
        channelType: REWARDS_REFERRAL_SHARE_CHANNELS_TYPE
    ): void =>
        this.windowPopupManager.open(
            getShareUrl(
                REFERRAL_SHARE_PATH,
                { channelType },
                this.tokenManagerModule.exports.tokenManager
            )
        );

    private getReward = (
        reward: RewardsReferralProgramRewardEntity,
        currencySymbol: string,
        freeProductTitle?: string
    ) => {
        if (!reward) return "";

        let result = "";

        switch (reward.type) {
            case REWARDS_REWARD_TYPES.giftCard:
                result = `${currencySymbol}${reward.initialValue} gift card`;
                break;

            case REWARDS_REWARD_TYPES.discount:
                if (reward.discountType === REWARDS_DISCOUNT_TYPES.fixedAmount)
                    result = `${currencySymbol}${Math.abs(
                        reward.discountValue ?? 0
                    )} off discount`;

                if (reward.discountType === REWARDS_DISCOUNT_TYPES.percentage)
                    result = `${Math.abs(
                        reward.discountValue ?? 0
                    )}% off discount`;

                if (reward.discountType === REWARDS_DISCOUNT_TYPES.freeShipping)
                    result = "free shipping";

                break;

            case REWARDS_REWARD_TYPES.freeProduct:
                result = `${Math.abs(reward.discountValue ?? 0)}% discount on ${
                    freeProductTitle ?? "free product"
                }`;
                break;
        }

        return result;
    };

    private replaceEmailPlaceholders(
        text: string,
        replacements: { [key: string]: string }
    ): string {
        return text.replace(
            /{{(.*?)}}/g,
            (_, key: string) => replacements[key.trim()]
        );
    }

    public selectMailToLink = ({
        shopName,
        currencySymbol,
        referralData$,
        referralShareChannels$,
        receiverReward$,
        freeProductFromReceiverReward$: freeProduct$,
    }: {
        shopName: string;
        currencySymbol: string;
        referralData$: Observable<ProgramAccountEntity["referral"]>;
        referralShareChannels$: Observable<
            RewardsStorefrontValuesSettingsEntity["referralShareChannels"]
        >;
        receiverReward$: Observable<RewardsReferralProgramRewardEntity | null>;
        freeProductFromReceiverReward$: Observable<ProductEntity | null>;
    }) => {
        return combineLatest([
            referralShareChannels$,
            referralData$,
            receiverReward$,
            freeProduct$,
        ]).pipe(
            map(
                ([
                    referralShareChannels,
                    referralData,
                    receiverReward,
                    freeProduct,
                ]) => [
                    referralShareChannels.find(
                        (channel): channel is RewardsReferralEmailShareEntity =>
                            channel.channelType ===
                                REWARDS_REFERRAL_SHARE_CHANNELS.emailShare &&
                            channel.isEnabled
                    ),
                    referralData,
                    receiverReward,
                    freeProduct,
                ]
            ),
            filter(
                (
                    value
                ): value is [
                    RewardsReferralEmailShareEntity,
                    ProgramAccountEntity["referral"],
                    RewardsReferralProgramRewardEntity,
                    ProductEntity | null
                ] => !!value.length
            ),
            map(
                ([
                    emailShareChannel,
                    referralData,
                    receiverReward,
                    freeProduct,
                ]) => {
                    if (!emailShareChannel) return "";

                    const subject = this.replaceEmailPlaceholders(
                        emailShareChannel.title,
                        { shop_name: shopName }
                    );

                    const body = this.replaceEmailPlaceholders(
                        emailShareChannel.message,
                        {
                            shop_name: shopName,
                            link: referralData?.referralLink ?? "",
                            reward_type: this.getReward(
                                receiverReward,
                                currencySymbol,
                                freeProduct?.title
                            ),
                        }
                    );

                    return `mailto:?subject=${subject}&body=${body}`;
                }
            )
        );
    };

    public getReferralProgram =
        (): Observable<RewardsReferralProgramEntity> => {
            this.logger.debug(
                "RewardsReferralProgramService.getReferralProgram is started"
            );

            const status = this.referralProgramStore.getStatus();
            this.referralProgramStore.setStatus(getLoadingStatus(status));

            return this.referralProgramApiService
                .getReferralProgramReward()
                .pipe(
                    catchError((error: unknown) => {
                        this.logger.debug(
                            "RewardsReferralProgramService.getReferralProgram is errored",
                            error
                        );
                        this.referralProgramStore.setStatus(STATUS.ERROR);
                        throw error;
                    }),
                    tap((value) => {
                        this.logger.debug(
                            "RewardsReferralProgramService.getReferralProgram is ready"
                        );
                        //Store Update
                        applyTransaction(() => {
                            this.referralProgramStore.update((state) => ({
                                ...state,
                                referralProgram: value,
                            }));
                            this.referralProgramStore.setStatus(STATUS.READY);
                        });
                    })
                );
        };
}

export default RewardsReferralProgramService;
